import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import StepIndicator from '../gallery-creation/components/StepIndicator';
import PasswordSetupStep from './components/PasswordSetupStep';
import RoleConfirmationStep from './components/RoleConfirmationStep';
import WelcomeStep from './components/WelcomeStep';
import Icon from '../../components/AppIcon';

const ContributorSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState({
    fullName: '',
    password: '',
    confirmPassword: '',
    roleAccepted: false,
    termsAccepted: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const steps = [
    { id: 1, label: 'Welcome' },
    { id: 2, label: 'Set Password' },
    { id: 3, label: 'Confirm Role' }
  ];

  useEffect(() => {
    // Redirect if not authenticated or not a contributor
    if (!isAuthenticated) {
      navigate('/admin-login');
      return;
    }

    if (userProfile && userProfile?.role !== 'contributor') {
      navigate('/');
      return;
    }

    // Redirect if already completed setup
    if (userProfile && userProfile?.full_name && userProfile?.full_name?.trim() !== '') {
      navigate('/contributor-dashboard');
    }
  }, [isAuthenticated, userProfile, navigate]);

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    setIsLoading(true);
    try {
      // Update user profile with full name
      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.update({
          full_name: setupData?.fullName,
          first_time_login: false,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', user?.id);

      if (profileError) throw profileError;

      // Update password in auth
      const { error: passwordError } = await supabase?.auth?.updateUser({
        password: setupData?.password
      });

      if (passwordError) throw passwordError;

      // Send verification email for contributor
      try {
        const verificationUrl = `${window.location?.origin}/admin-login?verified=true`;
        await supabase?.functions?.invoke('send-verification-email', {
          body: {
            email: userProfile?.email,
            fullName: setupData?.fullName,
            verificationUrl,
            userType: 'contributor'
          }
        });
      } catch (emailError) {
        console.error('Failed to send verification email:', emailError);
        // Don't block setup completion if email fails
      }

      // Navigate to contributor dashboard
      navigate('/contributor-dashboard');
    } catch (error) {
      console.error('Setup completion error:', error);
      alert('Failed to complete setup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <WelcomeStep
            email={userProfile?.email}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <PasswordSetupStep
            setupData={setupData}
            setSetupData={setSetupData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <RoleConfirmationStep
            setupData={setupData}
            setSetupData={setSetupData}
            onComplete={handleComplete}
            onBack={handleBack}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="UserPlus" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">Contributor Setup</h1>
                <p className="text-xs text-muted-foreground">Complete your account activation</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-3xl mx-auto">
          {/* Step Indicator */}
          <StepIndicator currentStep={currentStep} steps={steps} />

          {/* Step Content */}
          <div className="bg-card rounded-xl border border-border shadow-lg p-6 md:p-8">
            {renderStep()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContributorSetup;